import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaCalendarCheck,
  FaClock,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaDownload,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaStethoscope,
  FaFileInvoice,
  FaSyncAlt,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import AdminLayout from "../components/Layout/AdminLayout";

interface PendingAppointment {
  id: string;
  booking_id: string | null;
  appointmentDate: string;
  appointmentTime: string;
  rawDate: string;
  rawTime: string;
  department: string;
  status: string;
  fullName: string;
  age: string;
  dob: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  symptoms: string;
  report_url: string | null;
  payment_proof_url: string | null;
  created_at: string;
}

const ApproveAppointment: React.FC = () => {
  // const backendUrl = "http://localhost:8483";
  const backendUrl = "https://www.ayushvatika.com";

  const [appointments, setAppointments] = useState<PendingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [selectedAppt, setSelectedAppt] = useState<PendingAppointment | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [searchText, setSearchText] = useState("");

  const fetchPendingAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/admin/appointments/pending`);

      if (response.data?.success) {
        setAppointments(response.data.data || []);
      } else {
        setAppointments([]);
        toast.error(response.data?.message || "Pending appointments load nahi ho paaye.");
      }
    } catch (error: any) {
      console.error(error);
      setAppointments([]);
      toast.error(error.response?.data?.message || "Pending appointments load nahi ho paaye.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingAppointments();
  }, []);

  const filteredAppointments = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    if (!q) return appointments;

    return appointments.filter((appt) =>
      [
        appt.fullName,
        appt.phone,
        appt.email,
        appt.department,
        appt.appointmentDate,
        appt.appointmentTime,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [appointments, searchText]);

  const getFileUrl = (url?: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${backendUrl}${url}`;
  };

  const isImageFile = (url?: string | null) => {
    if (!url) return false;
    return /\.(png|jpg|jpeg|webp|gif)$/i.test(url);
  };

  const handleApprove = async (id: string) => {
    const confirmApprove = window.confirm(
      "Kya payment proof verify ho gaya hai? Appointment approve karna hai?"
    );

    if (!confirmApprove) return;

    try {
      setActionLoadingId(id);
      const response = await axios.put(`${backendUrl}/api/admin/appointments/${id}/approve`);
      toast.success(response.data?.message || "Appointment approved successfully.");
      setShowDetails(false);
      setSelectedAppt(null);
      await fetchPendingAppointments();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Approve failed.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const openReject = (appt: PendingAppointment) => {
    setSelectedAppt(appt);
    setRejectReason("Payment proof clear nahi hai / payment received nahi hua.");
    setShowRejectBox(true);
  };

  const handleReject = async () => {
    if (!selectedAppt) return;

    if (!rejectReason.trim()) {
      toast.error("Reject reason likhna zaroori hai.");
      return;
    }

    try {
      setActionLoadingId(selectedAppt.id);
      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/${selectedAppt.id}/reject`,
        { reason: rejectReason.trim() }
      );

      toast.success(response.data?.message || "Appointment rejected successfully.");
      setShowRejectBox(false);
      setShowDetails(false);
      setSelectedAppt(null);
      setRejectReason("");
      await fetchPendingAppointments();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Reject failed.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const openDetails = (appt: PendingAppointment) => {
    setSelectedAppt(appt);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setSelectedAppt(null);
    setRejectReason("");
    setShowDetails(false);
    setShowRejectBox(false);
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-black text-gray-900 md:text-3xl">
              Approve Appointments
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Payment proof verify karke appointment approve ya reject karein.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <FaSearch className="mr-3 text-gray-400" />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search patient, phone, dept..."
                className="w-full bg-transparent text-sm font-semibold text-gray-700 outline-none sm:w-72"
              />
            </div>

            <button
              onClick={fetchPendingAppointments}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow hover:bg-emerald-700 disabled:bg-gray-400"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5">
            <p className="text-sm font-bold text-orange-600">Pending Approval</p>
            <h2 className="mt-2 text-4xl font-black text-gray-900">{appointments.length}</h2>
          </div>

          <div className="rounded-2xl border border-emerald-100 bg-white p-5">
            <p className="text-sm font-bold text-emerald-600">Action Required</p>
            <h2 className="mt-2 text-xl font-black text-gray-900">Payment Verification</h2>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-white p-5">
            <p className="text-sm font-bold text-blue-600">Status Update</p>
            <h2 className="mt-2 text-xl font-black text-gray-900">User Side Auto Update</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[330px] items-center justify-center rounded-2xl border border-gray-200 bg-white text-emerald-700 shadow-sm">
            <div className="flex items-center gap-3 text-lg font-bold animate-pulse">
              <FaSyncAlt className="animate-spin" />
              Loading pending appointments...
            </div>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white py-20 text-center shadow-sm">
            <FaCalendarCheck className="mx-auto mb-4 text-gray-300" size={60} />
            <h2 className="text-xl font-bold text-gray-700">No Pending Appointments</h2>
            <p className="mt-1 text-gray-500">
              {searchText ? "Search ke according koi appointment nahi mila." : "Abhi koi appointment approval ke liye pending nahi hai."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="hidden grid-cols-12 gap-4 border-b border-gray-100 bg-gray-50 px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 lg:grid">
              <div className="col-span-3">Patient Details</div>
              <div className="col-span-2">Appointment</div>
              <div className="col-span-2">Department</div>
              <div className="col-span-2">Payment Proof</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredAppointments.map((appt) => (
                <div
                  key={appt.id}
                  className="grid grid-cols-1 gap-4 px-5 py-5 transition hover:bg-gray-50 lg:grid-cols-12 lg:items-center lg:px-6"
                >
                  <div className="lg:col-span-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                        <FaUser />
                      </div>

                      <div>
                        <h3 className="font-extrabold text-gray-900">{appt.fullName || "Patient"}</h3>
                        <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                          <FaPhone className="text-xs" />
                          {appt.phone || "N/A"}
                        </p>
                        {appt.email && (
                          <p className="mt-1 flex items-center gap-2 break-all text-sm text-gray-500">
                            <FaEnvelope className="text-xs" />
                            {appt.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="flex items-center gap-2 font-bold text-gray-800">
                      <FaCalendarCheck className="text-blue-500" />
                      {appt.appointmentDate}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-500">
                      <FaClock className="text-blue-400" />
                      {appt.appointmentTime}
                    </p>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="flex items-center gap-2 font-bold text-gray-800">
                      <FaStethoscope className="text-purple-500" />
                      {appt.department || "Not Assigned"}
                    </p>
                    <span className="mt-2 inline-flex rounded-full bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange-700">
                      Pending Approval
                    </span>
                  </div>

                  <div className="lg:col-span-2">
                    {appt.payment_proof_url ? (
                      <a
                        href={getFileUrl(appt.payment_proof_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100"
                      >
                        <FaFileInvoice />
                        View Proof
                      </a>
                    ) : (
                      <span className="inline-flex rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-bold text-red-700">
                        No Proof
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                    <button
                      type="button"
                      onClick={() => openDetails(appt)}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                    >
                      <FaEye />
                      Details
                    </button>

                    <button
                      type="button"
                      disabled={actionLoadingId === appt.id}
                      onClick={() => handleApprove(appt.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:bg-gray-400"
                    >
                      <FaCheckCircle />
                      Approve
                    </button>

                    <button
                      type="button"
                      disabled={actionLoadingId === appt.id}
                      onClick={() => openReject(appt)}
                      className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:bg-gray-400"
                    >
                      <FaTimesCircle />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showDetails && selectedAppt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">Appointment Details</h3>
                  <p className="mt-1 text-sm text-gray-500">Proof verify karke approve/reject karein.</p>
                </div>
                <button onClick={closeDetails} className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
                  <FaTimes />
                </button>
              </div>

              {selectedAppt.payment_proof_url && isImageFile(selectedAppt.payment_proof_url) && (
                <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                  <p className="mb-3 text-sm font-extrabold text-blue-700">Payment Screenshot Preview</p>
                  <img
                    src={getFileUrl(selectedAppt.payment_proof_url)}
                    alt="Payment proof"
                    className="max-h-[420px] w-full rounded-xl object-contain"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InfoCard label="Patient" value={selectedAppt.fullName} />
                <InfoCard label="Mobile" value={selectedAppt.phone} />
                <InfoCard label="Email" value={selectedAppt.email || "N/A"} />
                <InfoCard label="Department" value={selectedAppt.department || "N/A"} />
                <InfoCard label="Appointment Date" value={selectedAppt.appointmentDate} />
                <InfoCard label="Appointment Time" value={selectedAppt.appointmentTime} />
                <InfoCard label="Gender" value={selectedAppt.gender || "N/A"} />
                <InfoCard label="Age" value={selectedAppt.age || "N/A"} />

                <div className="rounded-xl bg-gray-50 p-4 md:col-span-2">
                  <p className="text-xs font-bold uppercase text-gray-400">Address</p>
                  <p className="font-bold text-gray-800">{selectedAppt.address || "N/A"}</p>
                </div>

                <div className="rounded-xl bg-gray-50 p-4 md:col-span-2">
                  <p className="text-xs font-bold uppercase text-gray-400">Symptoms</p>
                  <p className="text-sm font-medium leading-6 text-gray-700">{selectedAppt.symptoms || "No symptoms added."}</p>
                </div>

                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <p className="text-xs font-bold uppercase text-blue-500">Payment Proof</p>
                  {selectedAppt.payment_proof_url ? (
                    <a
                      href={getFileUrl(selectedAppt.payment_proof_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
                    >
                      <FaEye />
                      Open Payment Proof
                    </a>
                  ) : (
                    <p className="mt-2 font-bold text-red-600">Payment proof not uploaded.</p>
                  )}
                </div>

                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-xs font-bold uppercase text-gray-400">Medical Report</p>
                  {selectedAppt.report_url ? (
                    <a
                      href={getFileUrl(selectedAppt.report_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-gray-800 px-4 py-2 text-sm font-bold text-white hover:bg-gray-900"
                    >
                      <FaDownload />
                      Open Report
                    </a>
                  ) : (
                    <p className="mt-2 font-bold text-gray-500">No report uploaded.</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 md:flex-row md:justify-end">
                <button
                  disabled={actionLoadingId === selectedAppt.id}
                  onClick={() => handleApprove(selectedAppt.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  <FaCheckCircle />
                  Approve Appointment
                </button>

                <button
                  disabled={actionLoadingId === selectedAppt.id}
                  onClick={() => openReject(selectedAppt)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 font-bold text-white hover:bg-red-700 disabled:bg-gray-400"
                >
                  <FaTimesCircle />
                  Reject Appointment
                </button>
              </div>
            </div>
          </div>
        )}

        {showRejectBox && selectedAppt && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-extrabold text-gray-900">Reject Appointment</h3>
                <button onClick={() => setShowRejectBox(false)} className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
                  <FaTimes />
                </button>
              </div>

              <p className="mb-3 text-sm text-gray-500">
                Patient: <span className="font-bold text-gray-800">{selectedAppt.fullName}</span>
              </p>

              <label className="mb-2 block text-sm font-bold text-gray-700">Reject reason</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full rounded-2xl border border-gray-200 p-4 text-sm font-semibold text-gray-700 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                placeholder="Example: Payment proof clear nahi hai / payment received nahi hua"
              />

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowRejectBox(false)}
                  className="rounded-xl border border-gray-200 px-5 py-3 font-bold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoadingId === selectedAppt.id}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 font-bold text-white hover:bg-red-700 disabled:bg-gray-400"
                >
                  <FaTimesCircle />
                  Confirm Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const InfoCard: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-4">
    <p className="text-xs font-bold uppercase text-gray-400">{label}</p>
    <p className="break-words font-bold text-gray-800">{value || "N/A"}</p>
  </div>
);

export default ApproveAppointment;
